import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  DropdownSelect,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  Tag,
} from '@/components/ui';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { attachmentSizeError, partitionAttachmentFiles } from '@/lib/attachments';
import { useAttachmentUploader } from '@/lib/attachment-uploader';
import { takeSelectedFiles } from '@/lib/file-input';
import {
  getTicketFields,
  type TicketFieldRegistryField,
} from '@/lib/ticket-field-registry-api';
import { getTicketForm, getTicketForms, type TicketForm } from '@/lib/ticket-form-api';
import {
  DynamicTicketFieldRenderer,
  mergeTicketFormFields,
  validateDynamicFields,
  type DynamicTicketField,
} from '@/components/forms/DynamicTicketFieldRenderer';
import type { SearchableSelectOption } from '@/components/ui/searchable-select';

const DEFAULT_VALUES: Record<string, unknown> = {};

const SYSTEM_FIELD_KEYS = new Set([
  'requester',
  'subject',
  'description',
  'status',
  'priority',
  'source',
  'ticket_type',
  'agent',
  'group',
  'internal_agent',
  'internal_group',
  'company',
]);

function hasValue(value: unknown) {
  return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
}

export default function NewTicketPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { enqueue } = useAttachmentUploader();
  const [submitting, setSubmitting] = React.useState(false);
  const [loadingFields, setLoadingFields] = React.useState(true);
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [forms, setForms] = React.useState<TicketForm[]>([]);
  const [selectedFormId, setSelectedFormId] = React.useState('');
  const [form, setForm] = React.useState<TicketForm | null>(null);
  const [registryFields, setRegistryFields] = React.useState<TicketFieldRegistryField[]>([]);
  const [values, setValues] = React.useState<Record<string, unknown>>(DEFAULT_VALUES);
  const [showCc, setShowCc] = React.useState(false);
  const [ccEmails, setCcEmails] = React.useState<string[]>([]);
  const [files, setFiles] = React.useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [requesterOptions, setRequesterOptions] = React.useState<SearchableSelectOption[]>([]);
  const [requesterLoading, setRequesterLoading] = React.useState(false);

  const dynamicFields = React.useMemo(
    () => form ? mergeTicketFormFields(form, registryFields) : [],
    [form, registryFields],
  );

  React.useEffect(() => {
    let cancelled = false;

    async function loadInitialData() {
      try {
        setLoadingFields(true);
        const [nextForms, nextRegistry] = await Promise.all([getTicketForms(), getTicketFields()]);
        if (cancelled) return;
        setForms(nextForms);
        setRegistryFields(nextRegistry);
        const defaultForm = nextForms.find((candidate) => candidate.isDefault) ?? nextForms[0];
        if (!defaultForm) {
          setError(t('newTicket.noForms'));
          return;
        }
        setSelectedFormId(defaultForm.id);
        setForm(defaultForm);
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || t('newTicket.loadFormsError'));
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    }

    void loadInitialData();
    return () => {
      cancelled = true;
    };
  }, []);

  async function selectForm(formId: string) {
    if (!formId || formId === selectedFormId) return;
    setSelectedFormId(formId);
    setLoadingForm(true);
    setError(null);
    setFieldErrors({});
    try {
      const nextForm = await getTicketForm(formId);
      setForm(nextForm);
      setValues(DEFAULT_VALUES);
    } catch (loadError: any) {
      setError(loadError.message || t('newTicket.loadFormError'));
    } finally {
      setLoadingForm(false);
    }
  }

  const searchTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  function handleRequesterSearch(query: string) {
    clearTimeout(searchTimeout.current);
    if (query.length < 2) {
      setRequesterOptions([]);
      return;
    }
    setRequesterLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get<{ data: SearchableSelectOption[] }>('/contacts/search', {
          params: { q: query, type: 'agent' },
        });
        setRequesterOptions(res.data);
      } finally {
        setRequesterLoading(false);
      }
    }, 300);
  }

  function setValue(fieldKey: string, value: unknown) {
    setValues((current) => ({ ...current, [fieldKey]: value }));
    setFieldErrors((current) => {
      if (!current[fieldKey]) return current;
      const next = { ...current };
      delete next[fieldKey];
      return next;
    });
  }

  function fieldAddon(field: DynamicTicketField) {
    if (field.sourceType === 'default_requester') {
      return (
        <>
          {!showCc && (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="text-xs text-brand-600 hover:text-brand-800 hover:underline"
            >
              {t('newTicket.addCc')}
            </button>
          )}
          {showCc && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-ink-muted">{t('newTicket.cc')}</div>
              <DynamicTicketFieldRenderer
                fields={[{ ...field, fieldKey: '__cc__', label: t('newTicket.cc'), requiredForAgents: false }]}
                values={{}}
                mode="agent"
                onChange={(fieldKey, value) => {
                  if (fieldKey !== '__cc__') return;
                  const option = requesterOptions.find((candidate) => candidate.value === value);
                  const email = option?.sublabel || option?.label || '';
                  if (email.includes('@') && !ccEmails.includes(email)) setCcEmails((current) => [...current, email]);
                }}
                requesterOptions={requesterOptions}
                requesterLoading={requesterLoading}
                onRequesterSearch={handleRequesterSearch}
              />
              {ccEmails.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {ccEmails.map((email) => (
                    <Tag
                      key={email}
                      label={email}
                      onRemove={() => setCcEmails((current) => current.filter((entry) => entry !== email))}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      );
    }

    if (field.sourceType === 'default_description') {
      return (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(event) => {
              const selectedFiles = takeSelectedFiles(event.target);
              const { accepted, rejected } = partitionAttachmentFiles(selectedFiles);
              if (rejected.length > 0) setError(attachmentSizeError(rejected));
              if (accepted.length > 0) setFiles((current) => [...current, ...accepted]);
            }}
            className="hidden"
          />
          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex items-center justify-between rounded bg-surface-hover px-3 py-1.5 text-xs">
                  <span className="truncate">{file.name} ({(file.size / 1024).toFixed(0)} KB)</span>
                  <button type="button" onClick={() => setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))} className="text-ink-subtle hover:text-danger-600" aria-label={t('newTicket.removeFile', { name: file.name })}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      );
    }

    return null;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    const nextFieldErrors = validateDynamicFields(dynamicFields, values, 'agent', form?.rules, (label) => t('fields.required', { label }));
    if (!hasValue(values.requester)) nextFieldErrors.requester = t('newTicket.requesterRequired');
    if (!hasValue(values.subject)) nextFieldErrors.subject = t('newTicket.subjectRequired');
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      const customFields: Record<string, unknown> = {};
      for (const field of dynamicFields) {
        if (!SYSTEM_FIELD_KEYS.has(field.fieldKey) && hasValue(values[field.fieldKey])) {
          customFields[field.fieldKey] = values[field.fieldKey];
        }
      }

      const payload: Record<string, unknown> = {
        formId: selectedFormId,
        subject: values.subject,
        description: values.description || undefined,
        priority: values.priority || undefined,
        status: values.status || undefined,
        source: values.source || undefined,
        ticketType: values.ticket_type || undefined,
        requesterId: values.requester,
        assigneeId: values.agent || undefined,
        groupId: values.group || undefined,
        internalAgentId: values.internal_agent || undefined,
        internalGroupId: values.internal_group || undefined,
        companyId: values.company || undefined,
        ccEmails: ccEmails.length ? ccEmails : undefined,
        customFields: Object.keys(customFields).length ? customFields : undefined,
      };

      const res = await api.post<{ data: { id: string } }>('/tickets', payload);
      const ticketId = res.data.id;

      // ponytail: navigate immediately — attachments upload in background
      if (files.length > 0) {
        enqueue(ticketId, files);
      }

      navigate(`/tickets/${ticketId}`);
    } catch (submitError: any) {
      if (submitError.data?.errors) {
        setFieldErrors(
          Object.fromEntries(
            Object.entries(submitError.data.errors).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value]),
          ) as Record<string, string>,
        );
      } else {
        setError(submitError.message || t('newTicket.createFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        breadcrumb={[{ label: t('nav.tickets'), to: '/tickets' }, { label: t('newTicket.title') }]}
        title={t('newTicket.title')}
        description={t('newTicket.description')}
        actions={forms.length > 1 ? (
          <label className="min-w-64 text-sm font-medium text-ink-muted">
            {t('newTicket.form')}
            <div className="mt-1">
              <DropdownSelect
                options={forms.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
                value={selectedFormId}
                onChange={(value) => void selectForm(value)}
                disabled={loadingForm || submitting}
              />
            </div>
          </label>
        ) : undefined}
      />
      {error && <ErrorState className="mb-4" message={error} />}
      {loadingFields || loadingForm ? (
        <Card>
          <CardContent className="space-y-5 py-6" aria-busy="true">
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="space-y-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : !form ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              title={t('newTicket.noFormTitle')}
              description={t('newTicket.noFormDescription')}
            />
          </CardContent>
        </Card>
      ) : dynamicFields.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              title={t('newTicket.noFieldsTitle')}
              description={t('newTicket.noFieldsDescription')}
            />
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{form.name}</CardTitle>
              {form.description && <p className="text-sm text-ink-subtle">{form.description}</p>}
            </CardHeader>
            <CardContent className="space-y-4">
              <DynamicTicketFieldRenderer
                fields={dynamicFields}
                values={values}
                errors={fieldErrors}
                rules={form.rules}
                mode="agent"
                onChange={setValue}
                requesterOptions={requesterOptions}
                requesterLoading={requesterLoading}
                onRequesterSearch={handleRequesterSearch}
                onAttach={() => fileInputRef.current?.click()}
                fieldAddon={fieldAddon}
              />
            </CardContent>
          </Card>
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={submitting}>{submitting ? t('newTicket.creating') : t('newTicket.submit')}</Button>
          </div>
        </form>
      )}
    </div>
  );
}
